// Layout Component - Premium OptiTrajet Layout with elegant sidebar
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard, Route, Receipt, FileText, Truck,
  Calculator, Settings, LogOut, Menu, X, ChevronDown,
  Globe, User, ChevronRight, Shield, History,
  Wrench, Sun, Moon, QrCode, Navigation, Bell,
  Search, PanelLeftClose, PanelLeft
} from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import QRScanner from './QRScanner';

const Layout = ({ children }) => {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const { theme, toggleTheme, isDark } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [qrScannerOpen, setQrScannerOpen] = useState(false);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const navItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: t('nav.dashboard'), tourId: 'sidebar-dashboard' },
    { path: '/trips', icon: Route, label: t('nav.trips'), tourId: 'sidebar-trips' },
    { path: '/gps', icon: Navigation, label: 'GPS Pro', tourId: 'sidebar-gps', badge: 'Nouveau', badgeColor: 'primary' },
    { path: '/confidence', icon: Shield, label: 'Confiance', tourId: 'sidebar-confidence' },
    { path: '/expenses', icon: Receipt, label: t('nav.expenses'), tourId: 'sidebar-expenses' },
    { path: '/invoices', icon: FileText, label: t('nav.invoices'), tourId: 'sidebar-invoices' },
    { path: '/accounting', icon: Calculator, label: t('nav.accounting'), tourId: 'sidebar-accounting' },
    { path: '/accountant-portal', icon: Calculator, label: 'Comptable', tourId: 'sidebar-accountant', badge: 'Pro+', badgeColor: 'secondary' },
    { path: '/fleet', icon: Truck, label: t('nav.fleet'), requiresPlan: 'fleet', tourId: 'sidebar-fleet' },
    { path: '/maintenance', icon: Wrench, label: 'Maintenance', tourId: 'sidebar-maintenance' },
    { path: '/audit', icon: History, label: 'Audit', tourId: 'sidebar-audit' },
    { path: '/settings', icon: Settings, label: t('nav.settings'), tourId: 'sidebar-settings' },
  ];

  const languages = [
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'pt', name: 'Português', flag: '🇵🇹' },
    { code: 'it', name: 'Italiano', flag: '🇮🇹' },
    { code: 'ar', name: 'العربية', flag: '🇸🇦' },
  ];

  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('language', lang);
  };

  const currentLanguage = languages.find(l => l.code === i18n.language) || languages[0];

  // Breadcrumbs logic
  const getBreadcrumbs = () => {
    const currentItem = navItems.find(item => item.path === location.pathname);
    return [
      { path: '/dashboard', label: 'Accueil' },
      ...(currentItem && currentItem.path !== '/dashboard' ? [{ path: currentItem.path, label: currentItem.label }] : [])
    ];
  };

  const breadcrumbs = getBreadcrumbs();

  const SidebarContent = ({ collapsed = false }) => (
    <>
      {/* Logo */}
      <div className={`p-4 ${collapsed ? 'px-2' : 'px-5'} border-b border-border/50`}>
        <Link to="/dashboard" className="flex items-center gap-3 group" data-testid="sidebar-logo">
          <div className="relative w-10 h-10 flex-shrink-0">
            <div className="absolute inset-0 bg-primary/20 rounded-xl blur-lg group-hover:bg-primary/30 transition-colors" />
            <div className="relative w-10 h-10 bg-primary rounded-xl flex items-center justify-center shadow-lg shadow-primary/25 group-hover:shadow-primary/40 transition-all">
              <Route className="w-5 h-5 text-white" strokeWidth={2} />
            </div>
          </div>
          {!collapsed && (
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
            >
              <h1 className="text-lg font-semibold tracking-tight text-foreground">
                OptiTrajet
              </h1>
              <p className="text-xs text-muted-foreground capitalize">
                {user?.subscription_plan || 'free'}
              </p>
            </motion.div>
          )}
        </Link>
      </div>

      {/* Navigation */}
      <nav className={`flex-1 ${collapsed ? 'p-2' : 'p-3'} space-y-1 overflow-y-auto`}>
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          
          if (item.requiresPlan && user?.subscription_plan !== item.requiresPlan) {
            return null;
          }

          return (
            <Link 
              key={item.path} 
              to={item.path} 
              className="block" 
              data-tour={item.tourId}
              data-testid={`sidebar-link-${item.path.replace('/', '')}`}
            >
              <motion.div
                whileHover={{ x: collapsed ? 0 : 2 }}
                whileTap={{ scale: 0.98 }}
                className={`
                  flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200
                  ${collapsed ? 'justify-center' : ''}
                  ${isActive
                    ? 'bg-white dark:bg-slate-800 shadow-sm border border-border text-primary font-medium'
                    : 'text-muted-foreground hover:bg-white/60 dark:hover:bg-slate-800/60 hover:text-foreground'
                  }
                `}
                title={collapsed ? item.label : undefined}
              >
                <Icon className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-primary' : ''}`} strokeWidth={1.5} />
                {!collapsed && (
                  <>
                    <span className="flex-1 text-sm">{item.label}</span>
                    {item.badge && (
                      <Badge 
                        variant={item.badgeColor === 'primary' ? 'default' : 'secondary'}
                        className="text-[10px] px-1.5 py-0"
                      >
                        {item.badge}
                      </Badge>
                    )}
                  </>
                )}
              </motion.div>
            </Link>
          );
        })}
      </nav>

      {/* User Section */}
      <div className={`${collapsed ? 'p-2' : 'p-4'} border-t border-border/50`}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button 
              className={`
                w-full flex items-center gap-3 p-2 rounded-lg
                hover:bg-white/60 dark:hover:bg-slate-800/60 transition-colors
                ${collapsed ? 'justify-center' : ''}
              `}
              data-testid="user-menu-trigger"
            >
              <div className="w-9 h-9 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                <User className="w-4 h-4 text-primary" strokeWidth={1.5} />
              </div>
              {!collapsed && (
                <div className="flex-1 text-left min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">
                    {user?.first_name || user?.email?.split('@')[0]}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                </div>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align={collapsed ? "center" : "start"} className="w-56">
            <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="menu-settings">
              <Settings className="w-4 h-4 mr-2" />
              Paramètres
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout} className="text-destructive" data-testid="menu-logout">
              <LogOut className="w-4 h-4 mr-2" />
              Déconnexion
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </>
  );

  return (
    <div className="flex h-screen bg-background">
      {/* Desktop Sidebar */}
      <aside 
        className={`
          hidden lg:flex flex-col bg-[hsl(var(--sidebar-bg))] border-r border-border/50
          transition-all duration-300 ease-in-out
          ${sidebarCollapsed ? 'w-[72px]' : 'w-[260px]'}
        `}
      >
        <SidebarContent collapsed={sidebarCollapsed} />
        
        {/* Collapse Toggle */}
        <button
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className="absolute left-[calc(var(--sidebar-width)-12px)] top-20 z-50 w-6 h-6 bg-white dark:bg-slate-800 border border-border rounded-full flex items-center justify-center shadow-sm hover:shadow-md transition-all"
          style={{ '--sidebar-width': sidebarCollapsed ? '72px' : '260px' }}
          data-testid="sidebar-collapse-toggle"
        >
          {sidebarCollapsed ? (
            <PanelLeft className="w-3 h-3 text-muted-foreground" />
          ) : (
            <PanelLeftClose className="w-3 h-3 text-muted-foreground" />
          )}
        </button>
      </aside>

      {/* Mobile Sidebar Overlay */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="lg:hidden fixed inset-0 bg-black/50 z-40"
              onClick={() => setMobileMenuOpen(false)}
            />
            <motion.aside
              initial={{ x: -280 }}
              animate={{ x: 0 }}
              exit={{ x: -280 }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="lg:hidden fixed left-0 top-0 bottom-0 w-[280px] bg-[hsl(var(--sidebar-bg))] z-50 flex flex-col shadow-2xl"
            >
              <SidebarContent />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="glass-header sticky top-0 z-30">
          <div className="flex items-center justify-between px-4 lg:px-6 py-3">
            {/* Left Section */}
            <div className="flex items-center gap-4">
              {/* Mobile Menu Toggle */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden"
                data-testid="mobile-menu-toggle"
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
              
              {/* Breadcrumbs - Desktop */}
              <nav className="hidden md:flex items-center gap-1.5 text-sm">
                {breadcrumbs.map((crumb, index) => (
                  <React.Fragment key={crumb.path}>
                    {index > 0 && <ChevronRight className="w-4 h-4 text-muted-foreground/50" />}
                    <Link
                      to={crumb.path}
                      className={`
                        px-2 py-1 rounded-md transition-colors
                        ${index === breadcrumbs.length - 1
                          ? 'text-foreground font-medium bg-white/50 dark:bg-slate-800/50'
                          : 'text-muted-foreground hover:text-foreground hover:bg-white/50 dark:hover:bg-slate-800/50'
                        }
                      `}
                      data-testid={`breadcrumb-${crumb.path.replace('/', '') || 'home'}`}
                    >
                      {crumb.label}
                    </Link>
                  </React.Fragment>
                ))}
              </nav>

              {/* Page Title - Mobile */}
              <h1 className="md:hidden text-lg font-semibold text-foreground">
                {breadcrumbs[breadcrumbs.length - 1]?.label || 'Dashboard'}
              </h1>
            </div>

            {/* Right Section */}
            <div className="flex items-center gap-2">
              {/* Search - Desktop */}
              <div className="hidden lg:flex items-center">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Rechercher..."
                    className="w-64 pl-9 bg-white/50 dark:bg-slate-800/50 border-border/50 focus:bg-white dark:focus:bg-slate-800"
                    data-testid="header-search"
                  />
                </div>
              </div>

              {/* Theme Toggle */}
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="text-muted-foreground hover:text-foreground"
                data-testid="theme-toggle"
              >
                {isDark ? (
                  <Moon className="w-5 h-5" />
                ) : (
                  <Sun className="w-5 h-5" />
                )}
              </Button>

              {/* Language Selector */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="gap-2 text-muted-foreground hover:text-foreground"
                    data-testid="language-selector"
                  >
                    <Globe className="w-4 h-4" />
                    <span className="hidden sm:inline">{currentLanguage.flag}</span>
                    <ChevronDown className="w-3 h-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  {languages.map((lang) => (
                    <DropdownMenuItem
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className="cursor-pointer"
                      data-testid={`lang-${lang.code}`}
                    >
                      <span className="mr-2">{lang.flag}</span>
                      <span>{lang.name}</span>
                      {lang.code === i18n.language && (
                        <span className="ml-auto text-primary">✓</span>
                      )}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Notifications */}
              <Button
                variant="ghost"
                size="icon"
                className="relative text-muted-foreground hover:text-foreground"
                data-testid="notifications-btn"
              >
                <Bell className="w-5 h-5" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-primary rounded-full" />
              </Button>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
            className="p-4 lg:p-6 max-w-[1600px] mx-auto"
          >
            {children}
          </motion.div>
        </main>
      </div>

      {/* Floating QR Scanner Button */}
      <Button
        onClick={() => setQrScannerOpen(true)}
        className="fixed bottom-6 right-6 w-14 h-14 rounded-full bg-primary hover:bg-primary/90 shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30 z-40 transition-all"
        data-testid="qr-scanner-btn"
      >
        <QrCode className="w-6 h-6" />
      </Button>

      {/* QR Scanner Modal */}
      <QRScanner 
        isOpen={qrScannerOpen} 
        onClose={() => setQrScannerOpen(false)}
        onScanComplete={() => setQrScannerOpen(false)}
      />
    </div>
  );
};

export default Layout;
